import React, { useMemo } from 'react';
import { Box, Divider, Flex, TextV2 } from '@withjoy/joykit';
import { AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { useAccommodationRedirectUrl } from '@apps/accommodations/hooks/useAccommodationRedirectUrl';

import { ListingPhotos } from './ListingPhotos';
import { HeaderBlock } from './HeaderBlock';
import { TravelLogistics } from './TravelLogistics';
import { CostBreakdown } from './CostBreakdown';
import { HotelCTA } from './HotelCTA';
import { CouplesPick } from './CouplesPick';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useTranslation } from '@shared/core';
import { format } from 'date-fns';
import { UserNote } from './UserNote';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAccommodationsConsoleConfig } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { SoldOutTag } from '@shared/components/SoldOutTag/SoldOutTag';
import { useAccommodationMapListSync } from '../../AccommodationMapListSync/AccommodationMapListSync';
import { OtaProviderName } from './OtaProviderName';
import { getValidCoordinates } from '@shared/core/places';

const Container = ({ children, id, joyPlaceId }: { children: React.ReactNode; id?: string; joyPlaceId: Maybe<string> }) => {
  const { onHoverStart, onHoverEnd } = useAccommodationMapListSync();

  const handleMouseEnter = useEventCallback(() => {
    if (joyPlaceId) {
      onHoverStart(joyPlaceId);
    }
  });

  const handleMouseLeave = useEventCallback(() => {
    if (joyPlaceId) {
      onHoverEnd();
    }
  });

  return (
    <Flex
      id={id}
      flexDirection={{ _: 'column', md: 'row' }}
      columnGap={4}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </Flex>
  );
};

const ContentPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection="column"
      width={'100%'}
      gap={{ _: 4, md: 0 }}
      paddingY={{ _: 4, md: 0 }}
    >
      {children}
    </Flex>
  );
};

const LogisticsAndCostBlock = ({ children, alignRight }: { children: React.ReactNode; alignRight?: boolean }) => {
  return (
    <Flex
      flexDirection={{ _: 'column', md: 'row' }}
      gap={{ _: 4, md: 0 }}
      marginTop={{ _: 0, md: 6 }}
      justifyContent={{ _: 'flex-start', md: alignRight ? 'flex-end' : 'space-between' }}
      alignItems={{ _: 'none', md: 'center' }}
      width={{ _: '100%', md: 'auto' }}
    >
      {children}
    </Flex>
  );
};

const CutoffDate = ({ cutoffDate }: { cutoffDate: string }) => {
  const { t } = useTranslation('accommodationsConsole');
  const listingsPanelTrans = t('listingsPanel');
  const formattedCutoffDate = format(new Date(cutoffDate), 'MMMM do, yyyy');

  return (
    <Flex justifyContent={'flex-end'}>
      <TextV2
        typographyVariant="label1"
        color={'mono10'}
      >
        {listingsPanelTrans.cutoffDate({ cutoffDate: formattedCutoffDate })}
      </TextV2>
    </Flex>
  );
};

const ListingEntry = (props: { listing: AccommodationListingDTO; state: AccommodationsConsoleState; noOfNights: number }) => {
  const { listing, state, noOfNights } = props;
  const isMobileOrTablet = useIsMobileOrTablet();
  const getRedirectUrl = useAccommodationRedirectUrl(state);
  const config = useAccommodationsConsoleConfig();
  const redirectUrl = getRedirectUrl(listing);
  const telemetry = useListingsPanelTelemetry();
  const { tq } = useTranslation('accommodationsConsole');

  const handleSeeAvailability = useEventCallback(() => {
    telemetry.seeAvailabilityClicked({ joyPlaceId: listing.joyPlaceId || '', hotelName: listing.name });
  });

  const isSoldOut = listing.type === 'standardHotel' && listing.isSoldOutForSelectedDates;

  const handlePhotoClick = useEventCallback((e: React.MouseEvent) => {
    // Carousel controls stop propagation, so if this handler fires, it's safe to navigate
    // Only navigate if clicking on the image area (not on interactive carousel elements)
    const target = e.target as HTMLElement;
    const isCarouselControl = target.closest('button, [role="button"], [data-carousel-control]');
    if (!isCarouselControl && !isSoldOut && redirectUrl) {
      handleSeeAvailability();
      window.open(redirectUrl, '_blank');
    }
  });

  const pointOfInterest = useMemo(() => {
    const location = getValidCoordinates(state.circularRegionSearch);
    if (!location) return null;

    let displayName = state.searchLocation || tq`venue`;
    if (state.searchLocation === state.venue.name) displayName = tq`venue`;

    return { displayName, location };
  }, [state.circularRegionSearch, state.searchLocation, state.venue.name, tq]);

  const distanceFromTheVenue = distanceInMiles(pointOfInterest?.location, listing.location);
  const showLogisticsSection = !isMobileOrTablet && typeof distanceFromTheVenue === 'number';

  return (
    <Container
      id={`listing-${listing.joyPlaceId}`}
      joyPlaceId={listing.joyPlaceId}
    >
      <Box
        onTouchEnd={e => e.stopPropagation()}
        onTouchMove={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
        onClick={handlePhotoClick}
        cursor={!isSoldOut && redirectUrl ? 'pointer' : 'default'}
      >
        <ListingPhotos
          listing={listing}
          isMobileOrTablet={isMobileOrTablet}
        />
      </Box>
      <Box
        width={'100%'}
        {...(!isSoldOut && redirectUrl ? { as: 'a', href: redirectUrl, target: '_blank', onClick: handleSeeAvailability } : {})}
      >
        <Flex
          flexDirection="column"
          width={'100%'}
          height={'100%'}
          justifyContent={'space-between'}
        >
          <ContentPanel>
            {listing.isPickedByCouple && <CouplesPick listing={listing} />}
            <HeaderBlock
              listing={listing}
              pointOfInterest={pointOfInterest}
              isMobileOrTablet={isMobileOrTablet}
              redirectUrl={redirectUrl}
            />
            <OtaProviderName otaProvider={listing.otaProvider} />
            {listing.type === 'roomBlock' && listing.userNote && config?.listingsPanel?.listingEntry?.shouldShowRoomBlockNoteAndDate && <UserNote note={listing.userNote} />}
            {isMobileOrTablet && <Divider marginY={3} />}
            <LogisticsAndCostBlock alignRight={!showLogisticsSection}>
              {!isMobileOrTablet && pointOfInterest && (
                <TravelLogistics
                  pointOfInterest={pointOfInterest}
                  listing={listing}
                />
              )}
              <Flex
                flexDirection={'column'}
                gap={5}
              >
                {listing.type === 'roomBlock' && config?.listingsPanel?.listingEntry?.shouldShowRoomBlockNoteAndDate && <CutoffDate cutoffDate={listing.cutoffDate} />}
                {listing.pricingInfo.isLoading ? (
                  <CostBreakdown.Skeleton />
                ) : listing.pricingInfo.values ? (
                  <CostBreakdown
                    occupancies={state.occupancies}
                    pricingInfo={listing.pricingInfo.values}
                    noOfNights={noOfNights}
                  />
                ) : null}
              </Flex>
            </LogisticsAndCostBlock>
            {isSoldOut && (
              <Box marginTop={{ _: 0, md: 5 }}>
                <SoldOutTag />
              </Box>
            )}
          </ContentPanel>
          {redirectUrl && ((listing.type === 'standardHotel' && !listing.isSoldOutForSelectedDates) || listing.type === 'roomBlock') ? (
            <Flex
              justifyContent={'flex-end'}
              width={'100%'}
            >
              <HotelCTA
                listing={listing}
                redirectUrl={redirectUrl}
              />
            </Flex>
          ) : null}
        </Flex>
      </Box>
    </Container>
  );
};

ListingEntry.Skeleton = ({ isMobileOrTablet }: { isMobileOrTablet: boolean }) => {
  return (
    <Container joyPlaceId={null}>
      <ListingPhotos.Skeleton />
      <ContentPanel>
        <HeaderBlock.Skeleton isMobileOrTablet={isMobileOrTablet} />
        {isMobileOrTablet && <Divider marginY={3} />}
        <LogisticsAndCostBlock>
          {!isMobileOrTablet && <TravelLogistics.Skeleton />}
          <CostBreakdown.Skeleton />
        </LogisticsAndCostBlock>
      </ContentPanel>
    </Container>
  );
};

export { ListingEntry };
